
import { apiGet, apiPost, validateRequiredParams } from '@/services/BaseApiService';

const baseUrl = `/contact/`;


/**
 * Retrieves a contact by ID.
 * @param {string} id - The ID of the contact to retrieve.
 * @returns {Promise<any>} - A promise that resolves to the contact data.
 * @throws {Error} - If the required parameters are missing or an error occurs during the request.
 */
export const getContact = async (id) => {
    validateRequiredParams({ id });
    return apiGet(`${baseUrl}getcontact?id=${encodeURIComponent(id)}`);
};

export const getUserProfile = async () => {
    return apiGet(`${baseUrl}getuserprofile`); 
};


/**
 * Inserts a new contact.
 *
 * @async
 * @function insertContact
 * @param {Object} contact - The contact object to insert. Must implement a toJSON() method.
 * @returns {Promise<any>} A promise that resolves with the response from the API.
 * @throws {Error} If required parameters are missing or the API call fails.
 */
export const insertContact = async (contact) => {
    validateRequiredParams({ contact });
    return apiPost(`${baseUrl}insertcontact`, contact.toJSON());
};

/**
 * Updates an existing contact.
 * @param {object} contact - The contact object to update.
 * @returns {Promise<any>} - A promise that resolves to the response data.
 * @throws {Error} - If the required parameters are missing or an error occurs during the request.
 */
export const updateContact = async (contact) => {
    validateRequiredParams({ contact });
    return apiPost(`${baseUrl}updatecontact`, contact.toJSON());
};

/**
 * Retrieves the primary organizations associated with a contact.
 * @param {string} organizationId - The ID of the primary organization.
 * @returns {Promise<any>} - A promise that resolves to the primary organizations data.
 * @throws {Error} - If the required parameters are missing or an error occurs during the request.
 */
export const getContactPrimaryOrganizations = async (organizationId) => {
    validateRequiredParams({ organizationId });
    return apiGet(`${baseUrl}getcontactprimaryorganizations?id=${encodeURIComponent(organizationId)}`);
};

/**
 * Approves a contact by sending a POST request to the server.
 * @param {string} contactId - The ID of the contact to be approved.
 * @returns {Promise<any>} - A promise that resolves to the response data.
 * @throws {Error} - If the required parameters are missing or an error occurs during the request.
 */
export const approveContact = async (contactId) => {
    validateRequiredParams({ contactId });
    return apiPost(`${baseUrl}approvecontact?id=${encodeURIComponent(contactId)}`);
};

/**
 * Rejects a contact by sending a POST request to the server.
 * @param {string} contactId - The ID of the contact to be rejected.
 * @returns {Promise<any>} - A promise that resolves to the response data.
 * @throws {Error} - If the required parameters are missing or an error occurs during the request.
 */
export const rejectContact = async (contactId) => {
    validateRequiredParams({ contactId });
    return apiPost(`${baseUrl}rejectcontact?id=${encodeURIComponent(contactId)}`);
};
